import { css } from '@emotion/react';
import type { CSSInterpolation } from '@emotion/serialize';
import {
  forwardRef, type KeyboardEventHandler, type MouseEventHandler, type PropsWithChildren,
} from 'react';
import type { Theme } from '~/_shared/themes/theme.model';
import { mergeProps } from '~/_shared/utils/props/mergeProps';
import { AccordionHeaderSize } from '../accordion.types';
import { AccordionCaret } from './accordionCaret.component';

type AccordionItemHeadingProps = Readonly<{
  size?: AccordionHeaderSize;
}>;

export const ACCORDION_HEADER_SIZE_LARGE = 70;
export const ACCORDION_HEADER_SIZE_MEDIUM = 45;

const lockedSummarySelector = '[data-is-locked] > summary, [data-is-locked] > summary *';
const interactiveElementSelector = 'input, button, button > *, label, label > *';

const headerItemButtonSizeStyleMap = {
  [AccordionHeaderSize.Large]: {
    height: ACCORDION_HEADER_SIZE_LARGE,
    padding: '0 16px',
  },
  [AccordionHeaderSize.Medium]: {
    height: ACCORDION_HEADER_SIZE_MEDIUM,
    padding: '0 16px',
  },
  [AccordionHeaderSize.Flexible]: {
    minHeight: ACCORDION_HEADER_SIZE_MEDIUM,
    paddingBlock: 4,
    paddingInline: 16,
  },
} as const satisfies Readonly<Record<AccordionHeaderSize, CSSInterpolation>>;

const accordionHeadingStyles = (size: AccordionHeaderSize) =>
  (theme: Theme) => {
    return css({
      '--accordionItemBorderColor': theme.accordionColors.borderColor,
      position: 'relative',
      outline: 'none',
      display: 'flex',
      gap: 10,
      alignItems: 'center',
      justifyContent: 'space-between',
      backgroundColor: theme.accordionColors.headingBackground,
      cursor: 'default',
      ...headerItemButtonSizeStyleMap[size],
      '&:hover:not([data-is-locked] > summary)': {
        backgroundColor: theme.accordionColors.headingBackgroundHover,
      },
      '&:focus-visible::after': {
        content: '""',
        position: 'absolute',
        boxShadow: `inset 0 0 4px 4px ${theme.accordionColors.headingBackgroundActiveHover}`,
        pointerEvents: 'none',
        inset: 0,
      },
      ['[data-element="accordion-item"][open] > &:hover:not([data-is-locked] > summary)']: {
        backgroundColor: theme.accordionColors.headingBackgroundActiveHover,
      },
      ['[data-element="accordion-item"][open] > &, [data-element="accordion-item"][open] > &:focus']: {
        '--accordionItemBorderColor': theme.accordionColors.headingBorderActive,
        borderBottom: '1px solid var(--accordionItemBorderColor)',
        boxShadow: `0px 1px 0px 0px inset ${theme.accordionColors.headingBorderActive}`,
        backgroundColor: theme.accordionColors.headingBackgroundActive,
      },
      ['[data-element="accordion-item"] + [data-element="accordion-item"] > &, [data-element="accordion-item"] + [data-element="accordion-item"] > &:focus']: {
        borderTop: '1px solid var(--accordionItemBorderColor)',
        boxShadow: 'none',
      },
    });
  };

const headerContentStyle = css({
  width: '100%',
  minWidth: 0,
});

const expandDisablerProps = {
  onClick: (e: Parameters<MouseEventHandler>[0]) => {
    if ((e.target as HTMLElement)?.matches(lockedSummarySelector) &&
      !(e.target as HTMLElement)?.matches(interactiveElementSelector)) {
      e.preventDefault();
    } // block mouse click toggle
  },
  onKeyDown: (e: Parameters<KeyboardEventHandler>[0]) => {
    if ((e.target as HTMLElement)?.matches(lockedSummarySelector)) {
      if ((e.key === ' ') || (e.key === 'Enter')) {
        e.preventDefault(); // block Space and Enter key toggle
      }
    }
  },
};
type SummaryProps = React.DetailedHTMLProps<React.HTMLAttributes<HTMLElement>, HTMLElement>;

export const AccordionItemHeading =
  forwardRef<HTMLElement, PropsWithChildren<AccordionItemHeadingProps> & SummaryProps>(({
    children,
    size = AccordionHeaderSize.Medium,
    ...props
  }, ref) => {
    return (
      <summary
        {...mergeProps(expandDisablerProps, props)}
        css={accordionHeadingStyles(size)}
        data-element="accordion-heading"
        ref={ref}
      >
        <div
          data-element="accordion-heading-content"
          css={headerContentStyle}
        >
          {children}
        </div>
        <AccordionCaret />
      </summary>
    );
  });

AccordionItemHeading.displayName = 'AccordionItemHeading';
